import { z } from 'zod';

import { CompareViewSchema, DiffStatusSchema, SourceSystemSchema } from '../enums.js';

import { UuidSchema } from './common.js';

// compare-service: manual compare, CSV upload, CSV export (spec §5.3, informational only).

export const ManualCompareSideSchema = z.union([
  z.object({ system: SourceSystemSchema, assemblyId: UuidSchema, rev: z.string().min(1) }),
  z.object({ uploadId: z.string().min(1) }),
]);
export type ManualCompareSide = z.infer<typeof ManualCompareSideSchema>;

export const ManualCompareRequestSchema = z.object({
  a: ManualCompareSideSchema,
  b: ManualCompareSideSchema,
  view: CompareViewSchema.default('engineering'),
  /** Manual view only; detection follows the org setting (ADR 0001 #9). */
  ignoreLeadingZeros: z.boolean().default(false),
});
export type ManualCompareRequest = z.infer<typeof ManualCompareRequestSchema>;

/** One row of the Compare grid, ready for the tree view. */
export const CompareRowSchema = z.object({
  key: z.string(),
  /** Parent row key; empty for top-level rows. */
  pkey: z.string(),
  depth: z.number().int().nonnegative(),
  pn: z.string(),
  description: z.string(),
  status: DiffStatusSchema,
  qtyA: z.number().nullable(),
  qtyB: z.number().nullable(),
  delta: z.number(),
  findNo: z.string().nullable(),
  seqNo: z.string().nullable(),
  note: z.string(),
});
export type CompareRow = z.infer<typeof CompareRowSchema>;

export const ManualCompareResponseSchema = z.object({
  labelA: z.string(),
  labelB: z.string(),
  view: CompareViewSchema,
  rows: z.array(CompareRowSchema),
  counts: z.record(DiffStatusSchema, z.number().int().nonnegative()),
});
export type ManualCompareResponse = z.infer<typeof ManualCompareResponseSchema>;

/** 200 response of `POST /compare/uploads`. A malformed file gets 422 `FILE_REJECTED`. */
export const UploadResponseSchema = z.object({
  uploadId: z.string(),
  name: z.string(),
  lineCount: z.number().int().nonnegative(),
});
export type UploadResponse = z.infer<typeof UploadResponseSchema>;

/** `POST /compare/export` returns `bom_compare.csv`. */
export const CompareExportRequestSchema = ManualCompareRequestSchema.extend({
  /** Only export rows with these statuses; all when omitted. */
  statuses: z.array(DiffStatusSchema).optional(),
});
export type CompareExportRequest = z.infer<typeof CompareExportRequestSchema>;
